/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ReactNode, type FC, useCallback } from 'react';

import classNames from 'classnames';
import { Layout } from '@adp-common/chat-uikit-shared';
import { I18n, type I18nKeysNoOptionsType } from '@adp-arch/i18n';
import { IconCozBroom } from '@coze-arch/coze-design/icons';
import { Modal } from '@coze-arch/coze-design';

import { UIKitTooltip } from '../../../../common/tooltips';
import { OutlinedIconButton } from '../../../../common';

interface IProps {
  isDisabled?: boolean;
  tooltipContent?: ReactNode;
  onClick: () => void;
  layout: Layout;
  className: string;
  showBackground: boolean;
}

const ClearHistoryButton: FC<IProps> = props => {
  const {
    isDisabled,
    tooltipContent,
    onClick,
    layout,
    className,
    showBackground,
  } = props;

  const handleClick = useCallback(() => {
    Modal.confirm({
      title: I18n.t('clear_history_confirm_title' as I18nKeysNoOptionsType),
      content: I18n.t('clear_history_confirm_content' as I18nKeysNoOptionsType),
      okText: I18n.t('Confirm'),
      cancelText: I18n.t('Cancel'),
      okButtonProps: { type: 'danger' as const },
      onOk: () => {
        onClick();
      },
    });
  }, [onClick]);

  return (
    <UIKitTooltip
      content={tooltipContent}
      hideToolTip={layout === Layout.MOBILE}
    >
      <OutlinedIconButton
        data-testid="bot-edit-debug-chat-clear-button"
        showBackground={showBackground}
        disabled={isDisabled}
        icon={<IconCozBroom className="text-18px" />}
        size="default"
        onClick={handleClick}
        className={classNames('mr-12px', '!rounded-full', className)}
      />
    </UIKitTooltip>
  );
};

export default ClearHistoryButton;
